<script lang="ts" setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import SealsSection from "@/views/trips/components/SealsSection.vue";
import DeleteSealDialog from "@/views/trips/components/dialogs/DeleteSealDialog.vue";
import { generateId } from "@/utils/generateId";
import { getTravelOrderRouteAddress } from "@/utils/routes";
import type { RouteStopDto, TravelOrderDto } from "@/api/trips/dto/trip.dto";
import type { StopInfoSeal, StopInfoSealsSection, TripRouteStop } from "@/types/trip";

const { t } = useI18n();

const seals = defineModel<StopInfoSeal[]>({ default: () => [] });

const props = withDefaults(
  defineProps<{
    tripId: string | number;
    travelOrders: TravelOrderDto[];
    routeStops: (TripRouteStop | RouteStopDto)[];
    errors: Record<string, string | null>;
    disabled?: boolean;
    hideTitle?: boolean;
  }>(),
  {
    disabled: false,
    hideTitle: false,
  },
);

const emit = defineEmits<{
  (e: "deleted", seal: StopInfoSeal): void;
}>();

const isDeleteDialogOpen = ref<boolean>(false);
const sealToDelete = ref<StopInfoSeal | null>(null);

const travelOrderIds = computed<number[]>(() => {
  return props.travelOrders.map((travelOrder) => travelOrder.id).filter((id): id is number => Boolean(id));
});

const sections = computed<StopInfoSealsSection[]>(() => {
  return travelOrderIds.value.map((travelOrderId) => {
    return {
      travelOrderId,
      label: getSectionLabel(travelOrderId),
      seals: seals.value.filter((seal) => seal.travelOrderId === travelOrderId),
    };
  });
});

function getSectionLabel(travelOrderId: number): string {
  const routeAddress = getTravelOrderRouteAddress(props.routeStops, travelOrderId);

  if (!routeAddress) {
    return t("trip.travel-order-short-number", { id: travelOrderId });
  }

  return t("trip.travel-order-route", { id: travelOrderId, route: routeAddress });
}

function addSeal(travelOrderId: number): void {
  seals.value = [
    ...seals.value,
    {
      id: null,
      tempId: generateId(),
      number: "",
      intact: true,
      travelOrderId,
    },
  ];
}

function updateSeal(updatedSeal: StopInfoSeal): void {
  seals.value = seals.value.map((seal) => {
    return seal.tempId === updatedSeal.tempId ? updatedSeal : seal;
  });
}

function removeSeal(removedSeal: StopInfoSeal): void {
  seals.value = seals.value.filter((seal) => seal.tempId !== removedSeal.tempId);
}

function deleteSeal(deletedSeal: StopInfoSeal): void {
  if (!deletedSeal.id) {
    removeSeal(deletedSeal);

    return;
  }

  sealToDelete.value = deletedSeal;
  isDeleteDialogOpen.value = true;
}

function onSealDeleted(deletedSeal: StopInfoSeal): void {
  removeSeal(deletedSeal);
  emit("deleted", deletedSeal);
}
</script>

<template>
  <div>
    <div v-if="!props.hideTitle" class="font-size-16 font-weight-500 mb-4 text-grey-navy">
      {{ t("trip.seals") }}
    </div>

    <omni-tooltip :disabled="!props.disabled" full-width>
      <template #activator>
        <seals-section
          v-for="section in sections"
          :key="section.travelOrderId"
          :disabled="props.disabled"
          :errors="errors"
          :label="section.label"
          :seals="section.seals"
          :travel-order-id="section.travelOrderId"
          @add="addSeal"
          @delete="deleteSeal"
          @update="updateSeal"
        />
      </template>

      {{ t("trip.seals-locked") }}
    </omni-tooltip>

    <delete-seal-dialog
      v-if="sealToDelete"
      v-model="isDeleteDialogOpen"
      :seal="sealToDelete"
      :trip-id="props.tripId"
      @deleted="onSealDeleted"
    />
  </div>
</template>

<style lang="scss" scoped></style>
